import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Link } from 'react-router-dom';

/**
 * Props for the DataGridLinkCell component.
 */
interface DataGridLinkCellProps {
  /** The cell parameters object provided natively by the DataGrid renderCell function */
  params: GridRenderCellParams;
  /** The base URL path for the navigation target destination (e.g., '/tenant/contracts') */
  basePath: string;
  /** The specific row object key to extract and append as the entity ID to the URL. Default: 'id' */
  idKey?: string;
  /** When true, passes the current row data and an item replication flag to the router history state */
  passDuplicateState?: boolean;
  /** Optional custom key-value pairs to extend or override properties in the history navigation state */
  customState?: Record<string, any>;
}

/**
 * A reusable component to render hyperlinked text within a DataGrid cell.
 * Handles client-side routing via react-router-dom natively, preventing layout reloads
 * and supporting explicit state payload forwarding.
 *
 * @example
 * ```tsx
 * {
 *   field: 'contract_header_code',
 *   headerName: t('entities.contract-equipment.contract_code'),
 *   flex: 1,
 *   renderCell: (params) => (
 *     <DataGridLinkCell
 *       params={params}
 *       basePath="/tenant/contracts"
 *       idKey="contract_header"
 *       passDuplicateState
 *     />
 *   )
 * }
 * ```
 */
export const DataGridLinkCell = ({
  params,
  basePath,
  idKey = 'id',
  passDuplicateState = false,
  customState = {}
}: DataGridLinkCellProps) => {
  const idValue = params.row[idKey];
  const cellValue = params.value;

  if (cellValue === undefined || cellValue === null || cellValue === '') {
    return null;
  }

  const navigationState = {
    ...(passDuplicateState ? { duplicateData: params.row, isDuplicate: true } : {}),
    ...customState
  };

  const absoluteBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`;
  const targetPath = absoluteBasePath.endsWith('/')
    ? `${absoluteBasePath}${idValue}`
    : `${absoluteBasePath}/${idValue}`;

  return (
    <Link
      to={targetPath}
      state={navigationState}
      style={{
        color: '#0288d1',
        cursor: 'pointer',
        fontWeight: 500,
        textDecoration: 'none',
        position: 'relative',
        display: 'inline-block',
        transition: 'color 0.2s ease-in-out'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = '#01579b';
        e.currentTarget.style.textDecoration = 'underline';
        e.currentTarget.style.textUnderlineOffset = '4px';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = '#0288d1';
        e.currentTarget.style.textDecoration = 'none';
      }}>
      {cellValue}
    </Link>
  );
};

DataGridLinkCell.displayName = 'DataGridLinkCell';
